import "@/env";

import type { ReactElement, ReactNode } from "react";
import { useEffect } from "react";
import type { AppProps } from "next/app";
import type { NextPage } from "next";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import "@/styles/globals.scss";
import Head from "next/head";
import { Toaster } from "@/components/ui/sonner";
import EmailToastTrigger from "@/components/EmailToastTrigger";
import ReferralToastTrigger from "@/components/ReferralToast/ReferralToastTrigger";
import SweepstakesTrigger from "@/components/Sweepstakes/SweepstakesTrigger";
import ImpactIdentify from "@/components/ImpactIdentify";
import OpenAIPageView from "@/components/OpenAIPageView";
import PinterestPageVisit from "@/components/PinterestPageVisit";
import PodscribeView from "@/components/PodscribeView";
import ConsentBanner from "@/consent/ConsentBanner";
import ConsentScripts from "@/consent/ConsentScripts";
import RedesignLayout from "@/chrome/RedesignLayout";
import { captureAcquisition } from "@/utils/acquisition";
import { rememberMetaClick } from "@/analytics/meta";
import { cleanupDeclinedStorage, getConsent } from "@/consent/consent";
import { useCrossPageAnchorGlide } from "@/hooks/useCrossPageAnchorGlide";
import { usePageTransition } from "@/hooks/usePageTransition";
import { useRouteTransition } from "@/hooks/useRouteTransition";
import { config } from "@/config";
import LiveAnnouncer from "@/components/common/LiveAnnouncer";

export type NextPageWithLayout<P = object, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

export default function App({ Component, pageProps }: AppPropsWithLayout) {
  const getLayout =
    Component.getLayout ?? ((page) => <RedesignLayout>{page}</RedesignLayout>);

  useRouteTransition();
  useCrossPageAnchorGlide();
  usePageTransition();

  useEffect(() => {
    if (getConsent().marketing) {
      captureAcquisition();
      rememberMetaClick();
    }
  }, []);

  // Initialize PostHog
  useEffect(() => {
    // The outgoing SDK's unload-time persistence write can race a
    // revocation's pre-reload cleanup and resurrect its cookie.
    const consent = getConsent();
    if (!consent.analytics || !consent.marketing) {
      cleanupDeclinedStorage(consent);
    }

    const sessionRecordingConfig = {
      maskAllInputs: true,
      recordCanvas: false,
      sampleRate: 0.05,
    };
    posthog.init(config.posthog.key, {
      api_host: "https://a.maticrobots.com",
      ui_host: "https://us.posthog.com",
      defaults: "2025-05-24",
      capture_exceptions: true,
      // A click that changes nothing on screen is the cheapest frustration
      // signal there is, and it costs one line to collect.
      capture_dead_clicks: true,
      debug: process.env.NODE_ENV === "development",
      loaded: () => {
        window.dispatchEvent(new Event("posthog:loaded"));
      },
      session_recording: sessionRecordingConfig,
      // Declined analytics → flags-only: /flags is not gated by opt-out, so
      // experiments keep rendering; nothing is captured or persisted.
      ...(!getConsent().analytics && {
        persistence: "memory" as const,
        opt_out_capturing_by_default: true,
        disable_session_recording: true,
      }),
    });
  }, []);

  const page = getLayout(<Component {...pageProps} />);
  const shellContent = (
    <PostHogProvider client={posthog}>
      <LiveAnnouncer />
      {page}
      <Toaster
        id="global"
        position="bottom-right"
        // Clear of a pinned purchase bar (globals.scss), at sonner's own gaps.
        offset={{ bottom: "calc(var(--pinned-bar-height) + 24px)" }}
        mobileOffset={{ bottom: "calc(var(--pinned-bar-height) + 16px)" }}
      />
      <EmailToastTrigger />
      <ReferralToastTrigger />
      <SweepstakesTrigger />
      {/* Before ImpactIdentify: a mid-session grant must inject `ire` first. */}
      <ConsentScripts />
      <ImpactIdentify />
      <PodscribeView />
      <PinterestPageVisit />
      <OpenAIPageView />
      <ConsentBanner />
    </PostHogProvider>
  );

  return (
    <>
      <Head>
        <meta
          name="viewport"
          content="initial-scale=1.0, width=device-width, maximum-scale=5"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href={`https://dt8esguquqvs0.cloudfront.net/assets/favicons/16x16.png`}
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href={`https://dt8esguquqvs0.cloudfront.net/assets/favicons/32x32.png`}
        />
        <link
          rel="icon"
          type="image/png"
          sizes="180x180"
          href={`https://dt8esguquqvs0.cloudfront.net/assets/favicons/180x180.png`}
        />
        <link
          rel="icon"
          type="image/png"
          sizes="192x192"
          href={`https://dt8esguquqvs0.cloudfront.net/assets/favicons/192x192.png`}
        />
      </Head>
      {shellContent}
    </>
  );
}
